import { useState } from "react";
import { Database, MoreVertical, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChatPanel } from "@/components/analyze/ChatPanel";
import { IndexProgress } from "@/components/analyze/IndexProgress";
import { useRagIndex } from "@/hooks/useRagIndex";
import { useRunEstimate } from "@/hooks/useRunEstimate";
import { formatRange } from "@/lib/formatRange";

interface Props {
  conceptId: string;
  paperIds: string[];
}

export function ChatToolPanel({ conceptId, paperIds }: Props) {
  const {
    indexStatus,
    isBuilding,
    buildProgress,
    buildError,
    buildIndex,
    clearIndex,
  } = useRagIndex(conceptId);
  const [dismissedProgress, setDismissedProgress] = useState(false);
  const est = useRunEstimate(conceptId, "chat", paperIds.length);

  const isIndexed = !!indexStatus?.is_indexed;

  const handleBuild = () => {
    setDismissedProgress(false);
    buildIndex(paperIds);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b bg-muted/20 px-3 py-1.5">
        {isIndexed ? (
          <>
            <span className="size-1.5 rounded-full bg-emerald-400" />
            <span className="text-xs text-muted-foreground">
              Chat index ready · {indexStatus.chunk_count} chunks ·{" "}
              {indexStatus.paper_count} papers
            </span>
          </>
        ) : (
          <>
            <span className="size-1.5 rounded-full bg-amber-400" />
            <span className="text-xs text-muted-foreground">
              {isBuilding
                ? "Building chat index…"
                : "Index not built — required to chat."}
            </span>
          </>
        )}
        <div className="ml-auto flex items-center gap-1">
          {!isIndexed && !isBuilding && (
            <Button
              size="xs"
              onClick={handleBuild}
              disabled={paperIds.length === 0}
            >
              <Database className="size-3.5" />
              Build index ({paperIds.length})
            </Button>
          )}
          {isIndexed && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Chat index actions">
                  <MoreVertical className="size-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-44 p-1">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
                  onClick={handleBuild}
                  disabled={isBuilding}
                >
                  <RefreshCw
                    className={`size-3.5 ${isBuilding ? "animate-spin" : ""}`}
                  />
                  Rebuild
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-red-400 hover:bg-red-500/10 disabled:opacity-50"
                  onClick={clearIndex}
                  disabled={isBuilding}
                >
                  <Trash2 className="size-3.5" />
                  Clear index
                </button>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      {!dismissedProgress && (isBuilding || buildProgress || buildError) && (
        <div className="border-b px-3 py-2">
          <IndexProgress
            isBuilding={isBuilding}
            progress={buildProgress}
            error={buildError}
            onDismiss={() => setDismissedProgress(true)}
          />
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-hidden">
        {isIndexed ? (
          <ChatPanel conceptId={conceptId} paperIds={paperIds} />
        ) : (
          <div className="flex h-full items-center justify-center p-8">
            <div className="text-center space-y-2 max-w-sm">
              <Database className="mx-auto size-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {isBuilding
                  ? "Indexing your corpus…"
                  : "Build the chat index to start asking the corpus."}
              </p>
              {!isBuilding && (
                <p className="text-xs text-muted-foreground">
                  Embeds {paperIds.length}{" "}
                  {paperIds.length === 1 ? "paper" : "papers"} ·{" "}
                  {est.data?.basis === "measured"
                    ? formatRange(est.data.low, est.data.high)
                    : "~2 minutes"}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
